import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import prescriptionService from '../services/prescriptionService';
import acteMedicalService from '../services/acteMedicalService';

const PrescriptionModal = ({ open, onClose, consultation, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    titre: '',
    description: '',
    type: 'pharma',
  });
  const [error, setError] = useState(null);
  const [actes, setActes] = useState([]);
  const [availableActes, setAvailableActes] = useState([]);
  const [selectedActe, setSelectedActe] = useState('');
  const [acteDetails, setActeDetails] = useState({
    quantite: 1,
    prix_unitaire: 0,
    posologie: '',
    duree_traitement: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchActes = async () => {
      try {
        const response = await acteMedicalService.getAllActesMedicaux();
        setAvailableActes(response.data);
      } catch (err) {
        console.error('Erreur lors du chargement des actes:', err);
      }
    };
    fetchActes();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.titre?.trim()) errors.titre = "Le titre est requis";
    if (!formData.description?.trim()) errors.description = "La description est requise";
    if (!formData.type) errors.type = "Le type est requis";
    if (actes.length === 0) errors.actes = "Au moins un acte médical est requis";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddActe = () => {
    if (!selectedActe) return;

    const acte = availableActes.find(a => a.id === selectedActe);
    if (!acte) return;

    const prix = parseFloat(acte.prix) || 0;

    setActes([...actes, {
      acte_medical_id: selectedActe,
      nom: acte.label,
      prix_unitaire: prix,
      quantite: acteDetails.quantite,
      posologie: acteDetails.posologie.trim(),
      duree_traitement: acteDetails.duree_traitement.trim()
    }]);

    // Reset form
    setSelectedActe('');
    setActeDetails({
      quantite: 1,
      prix_unitaire: 0,
      posologie: '',
      duree_traitement: ''
    });
  };

  const handleRemoveActe = (index) => {
    setActes(actes.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError(null);

    try {
      const prescriptionData = {
        prescription: {
          titre: formData.titre,
          description: formData.description,
          type: formData.type,
          assure_id: consultation.reception.assure.id,
          agent_clinique_id: consultation.medecin_clinique_id,
          consultation_id: consultation.id,
          matricule_assure: consultation.reception.assure.matricule,
          statut: 'en_attente'
        },
        actes: actes.map(acte => ({
          acte_medical_id: acte.acte_medical_id,
          quantite: acte.quantite,
          prix_unitaire: acte.prix_unitaire,
          posologie: acte.posologie || null,
          duree_traitement: acte.duree_traitement || null
        }))
      };

      await prescriptionService.createPrescriptionWithActes(prescriptionData);
      toast.success('Prescription créée avec succès');
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de la création de la prescription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography>Nouvelle Prescription</Typography>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            fullWidth
            label="Titre"
            value={formData.titre}
            onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
            required
            error={!!formErrors.titre}
            helperText={formErrors.titre}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            error={!!formErrors.description}
            helperText={formErrors.description}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              label="Type"
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
            >
              <MenuItem value="pharma">Pharmacie</MenuItem>
              <MenuItem value="labo">Laboratoire</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Actes Médicaux
          </Typography>

          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Acte Médical</InputLabel>
                <Select
                  value={selectedActe}
                  label="Acte Médical"
                  onChange={(e) => setSelectedActe(e.target.value)}
                >
                  {availableActes.map((acte) => (
                    <MenuItem key={acte.id} value={acte.id}>
                      {acte.label} - {acte.prix} FCFA
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Quantité"
                type="number"
                value={acteDetails.quantite}
                onChange={(e) => setActeDetails({ ...acteDetails, quantite: parseInt(e.target.value) })}
                sx={{ width: 100 }}
              />
            </Box>

            <TextField
              label="Posologie"
              value={acteDetails.posologie}
              onChange={(e) => setActeDetails({ ...acteDetails, posologie: e.target.value })}
            />

            <TextField
              label="Durée du traitement"
              value={acteDetails.duree_traitement}
              onChange={(e) => setActeDetails({ ...acteDetails, duree_traitement: e.target.value })}
            />

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddActe}
              disabled={!selectedActe}
            >
              Ajouter l'acte
            </Button>
          </Stack>

          {actes.length === 0 && formErrors.actes && (
            <Alert severity="error" sx={{ mt: 2 }}>{formErrors.actes}</Alert>
          )}

          {actes.length > 0 && (
            <Paper variant="outlined" sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
              <List>
                {actes.map((acte, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={acte.nom}
                      secondary={
                        `Quantité: ${acte.quantite} - Prix: ${acte.prix_unitaire.toLocaleString('fr-FR')} FCFA${acte.posologie ? ` - Posologie: ${acte.posologie}` : ''}${acte.duree_traitement ? ` - Durée: ${acte.duree_traitement}` : ''}`
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleRemoveActe(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading}>Annuler</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Création...' : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PrescriptionModal;
